export type User = {
  id: string;
  role: UserRole;
  userId: string;
  uid: string;
  given_name: string;
  family_name: string;
  nickname: string;
  name: string;
  picture: string;
  phone_number: string;
  email: string;
  email_verified: boolean;
  sub: string;
};

export interface IdToken {
  __raw: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  middle_name?: string;
  nickname?: string;
  preferred_username?: string;
  profile?: string;
  picture?: string;
  website?: string;
  email?: string;
  email_verified?: boolean;
  gender?: string;
  birthdate?: string;
  zoneinfo?: string;
  locale?: string;
  phone_number?: string;
  phone_number_verified?: boolean;
  address?: string;
  updated_at?: string;
  iss?: string;
  aud?: string;
  exp?: number;
  nbf?: number;
  iat?: number;
  jti?: string;
  azp?: string;
  nonce?: string;
  auth_time?: string;
  at_hash?: string;
  c_hash?: string;
  acr?: string;
  amr?: string[];
  sub_jwk?: string;
  cnf?: string;
  sid?: string;
  org_id?: string;
  org_name?: string;
  [key: string]: any;
}

export enum UserRole {
  Vendor = 'VENDOR', // Vendor Admin
  Team = 'TEAM', // Vendor Team Member
  Admin = 'ADMIN', // Brighte Admin
  Manager = 'MANAGER', // BDM
  SuperAdmin = 'SUPERADMIN', // Brighte Super Admin
  Client = 'CLIENT',
  AdminOperations = 'ADMIN_OPERATIONS',
}

export interface UserTokenPayload {
  role: UserRole;
  userId: string;
  vendors: string[];
  clientId: string;
  iss: string;
  sub: string;
  aud: string[];
  iat: number;
  exp: number;
  scope: string;
  id?: string;
}

export enum ErrorTypes {
  TOKEN_ERROR = 'TOKEN_ERROR',
}

export type TokenError = {
  status: ErrorTypes;
  message: string;
};

/**
 * Checks if an error is an authentication error that should not be retried.
 * Detects: 401 status, UNAUTHENTICATED code, TOKEN_ERROR, and invalid_grant from Auth0.
 */
export const isAuthError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const errorRecord = error as Record<string, unknown>;

  // TOKEN_ERROR from getAccessTokenSilently
  if (errorRecord.status === ErrorTypes.TOKEN_ERROR) return true;

  // invalid_grant from Auth0 token endpoint: { error: "invalid_grant", error_description: "..." }
  if (errorRecord.error === 'invalid_grant') return true;

  // invalid_grant wrapped in an Error message
  if (typeof errorRecord.message === 'string' && errorRecord.message.includes('invalid_grant')) return true;

  // GraphQL error response from graphql-request
  const response = errorRecord.response as Record<string, unknown> | undefined;
  if (response?.errors && Array.isArray(response.errors)) {
    return response.errors.some((graphqlError: Record<string, unknown>) => {
      const extensions = graphqlError?.extensions as Record<string, unknown> | undefined;

      // Check extension code
      if (extensions?.code === 'UNAUTHENTICATED') return true;

      // UNAUTHORIZED with 401 is an auth error, unless user is suspended
      if (extensions?.code === 'UNAUTHORIZED' && extensions?.statusCode === 401) {
        const message = extensions?.message as string | undefined;
        if (!message?.includes('suspended')) return true;
      }

      // statusCode 401 directly on extensions
      if (!extensions?.code && extensions?.statusCode === 401) return true;

      // statusCode 401 inside originalError (gateway pattern)
      const originalError = extensions?.originalError as Record<string, unknown> | undefined;
      if (originalError?.statusCode === 401) return true;

      // HTTP status in nested extension response
      const extResponse = extensions?.response as Record<string, unknown> | undefined;
      if (extResponse?.status === 401) return true;

      return false;
    });
  }

  // Direct HTTP 401 response
  if (errorRecord.status === 401) return true;

  return false;
};
